import Phaser from "phaser";

import {
  autoTile4bit_self_check_ever,
  hasKind_some_test,
} from "./art/autotiler";
import {
  Background_Chunk3,
  Background_Chunk2,
  Background_Chunk1,
  Background_Chunk4,
  Background_Chunk5,
  Background_Chunk6,
} from "./art/FX/background";
import { DoorPowerParticles } from "./art/FX/doorPowerParticles";
import data from "./artConfig.json";
import {
  BOARD_SIZE,
  GameEvents,
  getId,
  layerDepthDiff,
  SceneName,
  DEV_MODE,
  GAME_VARIABLES,
} from "./constants";
import CrateGameObj from "./entities/Crate";
import DoorEntity from "./entities/DoorEntity";
import ExtendedBlockEntity from "./entities/ExtendedBlockEntity";
import LazorToggleEntity from "./entities/LazorToggleEntity";
import MeleeEnemyEntity from "./entities/MeleeEnemy";
import Player, { Dir } from "./entities/Player";
import PlayerBullet from "./entities/PlayerBullet";
import PressurePlateEntity from "./entities/PressurePlateEntity";
import Teleporter from "./entities/Teleporter";
import TurnBasedInputPlugin from "./plugins/TurnBasedInputPlugin";
import {
  Crate,
  Entities,
  EntityKind,
  LazorToggle,
  PlayerBody,
} from "./state/entityKinds";
import GameState, { getXYindex } from "./state/gameState";
import { getDevEmitter, dev_event } from "../devUI/devEvents";
import { DatabaseHandle } from "./database/database";
import { GlobalEvents, EGlobalEvent } from "./plugins/GlobalGameEvents";
import { PlayerState } from "./state/playerStateJson";
import { ForeGround } from "./art/FX/ForeGroundParticles";
import { attachStaticWall } from "./art/wallPicker";
import { OneOffFx } from "./art/FX/oneOffFx";
import worldMap from "../../static/assets/worldMap.json";
import { addFloor } from "./art/bottomFloor";
import { SoundPlayer, MusicOptions } from "./sounds/SoundPlayer";
import { FailureModal } from "./gameSceneMisc/failureModal";
let sceneNum = 0;

const allLevels = {};

for (const levels of Object.values(worldMap)) {
  for (const level of levels) {
    allLevels[level.levelKey] = level;
  }
}

function sendPlayEvent(tilemapKey: string) {
  // Google analytics
  if (typeof window.ga === "function") {
    window.ga("send", "event", "GameScene", "play", tilemapKey);
  }
}

function sendCompleteEvent(tilemapKey: string, turnNumber: number) {
  // Google analytics
  if (typeof window.ga === "function") {
    window.ga(
      "send",
      "event",
      "GameScene",
      "completed",
      tilemapKey,
      turnNumber
    );
  }
}
function sendLeftEvent(tilemapKey: string, turnNumber: number) {
  // Google analytics
  if (typeof window.ga === "function") {
    window.ga("send", "event", "GameScene", "left", tilemapKey, turnNumber);
  }
}

function isChunk1(tilemapKey: string): boolean {
  if (worldMap.chunk1.some(({ levelKey }) => levelKey === tilemapKey)) {
    return true;
  }
  return false;
}
function isChunk3(tilemapKey: string): boolean {
  if (worldMap.chunk3.some(({ levelKey }) => levelKey === tilemapKey)) {
    return true;
  }
  return false;
}
function isChunk4(tilemapKey: string): boolean {
  if (worldMap.chunk4.some(({ levelKey }) => levelKey === tilemapKey)) {
    return true;
  }
  return false;
}
function isChunk5(tilemapKey: string): boolean {
  if (worldMap.chunk5.some(({ levelKey }) => levelKey === tilemapKey)) {
    return true;
  }
  return false;
}
function isChunk6(tilemapKey: string): boolean {
  if (worldMap.chunk6.some(({ levelKey }) => levelKey === tilemapKey)) {
    return true;
  }
  return false;
}

export default class StartScene extends Phaser.Scene {
  private currentGameState: GameState;
  private FXexplosionGroup: Phaser.GameObjects.Group | null;
  private FXexplosionActiveSet: Set<number>;
  private entities: Entities[] | undefined;
  private tilemapLevelKey: string;
  private player: Player | undefined;
  private failureModal: FailureModal;

  constructor(config: any) {
    super(config);
    this.currentGameState = new GameState();
    this.FXexplosionGroup = null;
    this.FXexplosionActiveSet = new Set();
    this.tilemapLevelKey = "Overworld";
  }

  init({ tilemapKey, entities }: { tilemapKey: string; entities: Entities[] }) {
    if (DEV_MODE) {
      // Used to skip intro for screenshots.
      GAME_VARIABLES.IS_RESTART = true;
    }
    this.tilemapLevelKey = tilemapKey;
    if (entities === undefined) {
      throw new Error("Started scene without any entities");
    }
    sendPlayEvent(this.tilemapLevelKey);
    // This is where we can pass in data constructed in other scenes.
    this.entities = entities;

    this.FXexplosionGroup = this.add.group({
      defaultKey: "FXexplosion",
      maxSize: 196,
      createCallback: (explosion) => {
        console.log("created", explosion);
      },
      removeCallback: (explosion) => {
        console.log("removed", explosion);
      },
    });
  }

  create() {
    SoundPlayer.registerScene(this);
    const sleep = (duration: number) => {
      return new Promise((resolve) => this.time.delayedCall(duration, resolve));
    };
    this.cameras.main.setBackgroundColor(0x3a293d);
    this.failureModal = new FailureModal(this);

    const turnBaseInput: TurnBasedInputPlugin = (this as any).myInput;
    this.events.once("transitionstart", () => {
      console.log("gameScene - transition Started");
      turnBaseInput.triggerForceDebounce();
    });
    this.events.once("transitioncomplete", async () => {
      if (GAME_VARIABLES.IS_RESTART) {
        // EZ Restart
        this.cameras.main.flash(
          GAME_VARIABLES.MOVE_TIME_MS * 6,
          255,
          255,
          255,
          true
        );
        turnBaseInput.removeForceDebounce();
      } else {
        // Animate in the game.
        SoundPlayer.enterLevelSFX(this);
        this.cameras.main.flash(3000, 255, 255, 255, true);
        this.cameras.main.centerOn(64, -40);
        this.cameras.main.pan(64, 64, 3000, "Sine.easeInOut", true);
        const bitmapOffset = 108;
        const bitmapBacker = this.add.sprite(
          128 / 2,
          128 / 2 - bitmapOffset,
          "atlas",
          "LevelTitle/level_title_backer"
        );
        bitmapBacker.setDepth(1000);
        bitmapBacker.setOrigin(0.5, 0.5);
        this.tilemapLevelKey;
        const bitmapText = this.add.bitmapText(
          128 / 2,
          128 / 2 - 2 - bitmapOffset,
          "bitPixelFont",
          ((allLevels as any)[this.tilemapLevelKey] as any)?.displayName ||
            this.tilemapLevelKey,
          0,
          Phaser.GameObjects.BitmapText.ALIGN_CENTER
        );
        bitmapText.setOrigin(0.5, 0.5);
        bitmapText.setDepth(2000);
        this.add.tween({
          targets: [bitmapBacker, bitmapText],
          y: `+=${bitmapOffset}`,
          duration: 3000,
          ease: "Sine.easeInOut",
        });

        this.add.tween({
          targets: [bitmapBacker, bitmapText],
          alpha: { from: 1, to: 0 },
          duration: 5000,
          delay: 1000,
          ease: "Quad.easeIn",
        });

        await sleep(2000);
        GAME_VARIABLES.IS_RESTART = false;
        turnBaseInput.removeForceDebounce();
      }
    });

    this.events.on(
      GameEvents.OneShotFX,
      ({
        x,
        y,
        key,
        playLaserToggle,
      }: {
        x: number;
        y: number;
        key: string;
        playLaserToggle: boolean | undefined;
      }) => {
        if (playLaserToggle) {
          SoundPlayer.laserToggleSFX(this);
        }
        OneOffFx.createFX(x, y, key, this, () => {});
      }
    );

    PlayerState.lastLevelEntered = this.tilemapLevelKey;
    const wonLevelHandler = async () => {
      PlayerState.unlockLevel(this.tilemapLevelKey);

      sendCompleteEvent(
        this.tilemapLevelKey,
        this.currentGameState.turn_counter
      );

      turnBaseInput.triggerForceDebounce();
      SoundPlayer.endLevelSFX(this);
      // Wait for state to settle...

      await sleep(10 + GAME_VARIABLES.MOVE_TIME_MS);
      const { x, y } = this.currentGameState.gameState.player as {
        x: number;
        y: number;
        dir: Dir;
      };

      let newX = x;
      let newY = y + 1;

      const index = getXYindex(newX, newY);
      let maxSleepTime = 0;
      for (const child of this.children.list) {
        if (child.type !== "Sprite") {
          continue;
        }
        const recastSprite = child as Phaser.GameObjects.Sprite;
        if (recastSprite.width > 60) {
          continue;
        }
        const spriteY = Math.floor(recastSprite.y / BOARD_SIZE);
        const spriteX = Math.floor(recastSprite.x / BOARD_SIZE);
        const timeline = this.tweens.createTimeline();
        const spriteDelay =
          Phaser.Math.Distance.Chebyshev(spriteX, spriteY, newX, newY) * 25;

        timeline.add({
          targets: recastSprite,
          y: `+=2`,
          ease: "Sine.easeInOut",
          duration: 150,
          delay: spriteDelay,
        });
        timeline.add({
          targets: recastSprite,
          y: `-=2`,
          ease: "Sine.easeInOut",
          duration: 150,
          offset: `+=25`,
        });
        maxSleepTime = Math.max(maxSleepTime, spriteDelay + 325);
        timeline.play();
      }

      await sleep(maxSleepTime + 20);

      this.cameras.main.flash(1500, 255, 255, 255, true);
      for (const child of this.children.list) {
        if (child.type !== "Sprite") {
          continue;
        }
        const recastSprite = child as Phaser.GameObjects.Sprite;
        if (Math.random() < 0.1) {
          recastSprite.y += 4;
        }
        recastSprite.y += 8;
      }

      // END initial ripple

      this.cameras.main.pan(64, 10, 3000, "Sine.easeIn", true);

      for (const child of this.children.list) {
        if (child.type !== "Sprite") {
          continue;
        }
        const recastSprite = child as Phaser.GameObjects.Sprite;
        const spriteY = Math.floor(recastSprite.y / BOARD_SIZE);
        const spriteX = Math.floor(recastSprite.x / BOARD_SIZE);

        this.add.tween({
          targets: recastSprite,
          y: `-=${
            -3 +
            Phaser.Math.Distance.Chebyshev(spriteX, spriteY, newX, newY) * 2
          }`,
          x: `${Math.random() > 0.5 ? "-=" : "+="}${
            Phaser.Math.Distance.Chebyshev(spriteX, spriteY, newX, newY) / 4
          }`,
          ease: "Sine.easeOut",
          duration: 3500,
        });
      }
      if (this.player) {
        this.add.tween({
          targets: this.player.sprite,
          y: `-=45`,
          duration: 3000,
          ease: "Sine.easeOut",
          onUpdate: (duration) => {
            const progress = duration.progress;
            this.player?.sprite.setTint(
              new Phaser.Display.Color(
                255 * (1 - progress),
                255 * (1 - progress),
                255 * (1 - progress)
              ).color
            );
          },
        });
      }
      // TODO: Add player teleporting away animation
      // await new Promise((resolve) => {
      //   this.cameras.main.flash(
      //     GAME_VARIABLES.MOVE_TIME_MS * 6,
      //     255,
      //     255,
      //     255,
      //     true,
      //     (_c, t) => (t > 0.99 ? resolve() : () => {})
      //   );
      // });
      await new Promise((resolve) => this.time.delayedCall(1500, resolve));
      this.cameras.main.zoomTo(1.25, 1600, "Cubic.easeIn", true);
      await new Promise((resolve) =>
        this.cameras.main.fadeOut(1600, 0, 0, 0, (_c, t) =>
          t > 0.99 ? resolve() : () => {}
        )
      );
      this.transitionToMap();
    };
    GlobalEvents.once(EGlobalEvent.WonLevel, wonLevelHandler, this);

    window.forceWinLevel = wonLevelHandler;

    getDevEmitter()?.emit(dev_event.START_RECORDING);

    if (isChunk1(this.tilemapLevelKey)) {
      new Background_Chunk1(this);
      SoundPlayer.switchSong(this, MusicOptions.city);
      new ForeGround(this);
    } else if (isChunk3(this.tilemapLevelKey)) {
      SoundPlayer.switchSong(this, MusicOptions.city);
      new Background_Chunk3(this);
      new ForeGround(this);
    } else if (isChunk4(this.tilemapLevelKey)) {
      new Background_Chunk4(this);
      SoundPlayer.switchSong(this, MusicOptions.sky);
      new ForeGround(this);
    } else if (isChunk5(this.tilemapLevelKey)) {
      new Background_Chunk5(this);
      SoundPlayer.switchSong(this, MusicOptions.stars);
    } else if (isChunk6(this.tilemapLevelKey)) {
      SoundPlayer.switchSong(this, MusicOptions.stars);
      new Background_Chunk6(this);
    } else {
      new Background_Chunk2(this);
      SoundPlayer.switchSong(this, MusicOptions.city);
      new ForeGround(this);
    }

    new DoorPowerParticles(this);
    const seededRandom = new Phaser.Math.RandomDataGenerator(
      "randomSeed9182309821309218" + this.tilemapLevelKey
    );
    this.currentGameState = new GameState();
    // All entities created should be created from the gameState...
    // Everything is stored from [0, 16). Then we scale it up based on pixels.

    this.entities?.forEach((obj: Entities) => {
      const objMap: any = obj;
      if (!objMap["kind"]) {
        console.warn("Entity is missing kind", obj);
        return;
      }
      const kind = obj.kind;
      let { x, y } = obj as {
        x: number;
        y: number;
      };

      if (kind === "PressurePlate") {
        new PressurePlateEntity(
          this.currentGameState,
          this,
          x,
          y,
          objMap.triggerId
        );
      } else if (kind === EntityKind.LazorToggle) {
        new LazorToggleEntity(this.currentGameState, this, obj as LazorToggle);
      } else if (kind === "Door") {
        new DoorEntity(
          this.currentGameState,
          this,
          x,
          y,
          objMap.triggerIdListener,
          objMap.isOpposite ? true : false
        );
      } else if (kind === "MeleeEnemy") {
        new MeleeEnemyEntity(this.currentGameState, this, x, y);
      } else if (kind === "SWall") {
        const idx = getXYindex(x, y);
        this.currentGameState.silentMutateGameState((state) => {
          state.board[idx].push({
            kind: EntityKind.StaticWall,
            id: -1,
            x,
            y,
          });
        });
      } else if (kind === "Empty") {
        // this.add.sprite(
        //   x * BOARD_SIZE,
        //   y * BOARD_SIZE,
        //   "atlas",
        //   "TileMap/2single_hole"
        // );
        this.currentGameState.silentMutateGameState((state) => {
          const idx = getXYindex(x, y);
          state.board[idx].push({
            kind: EntityKind.Empty,
            id: -1,
            x,
            y,
          });
        });
      } else if (kind === "Crate") {
        new CrateGameObj(this.currentGameState, this, obj as Crate);
      } else if (kind === EntityKind.ExtendedBlock) {
        new ExtendedBlockEntity(this.currentGameState, this, objMap);
      } else if (kind === "WinLevel") {
        this.currentGameState.silentMutateGameState((state) => {
          state.board[getXYindex(x, y)].push({
            kind: EntityKind.WinLevel,
            x,
            y,
            id: getId(),
            transitionToLevelKey: objMap.transitionToLevelKey,
            transitionHint: objMap.transitionHint,
            levelNumber: objMap.levelNumber,
          });
        });
        new Teleporter(this.currentGameState, this, x, y, objMap.levelNumber);
      } else if (kind === EntityKind.PlayerBody) {
        const obj2 = obj as PlayerBody;
        this.currentGameState.silentMutateGameState((state) => {
          state.player = {
            x,
            y,
            dir: obj2.dir as Dir,
          };
        });
      } else {
        throw new Error(`Missed a case with ${JSON.stringify(obj)}`);
      }
    });

    this.currentGameState.game_events = this.events;

    if (this.currentGameState.gameState.player) {
      this.player = new Player(this.currentGameState, this);
    }

    // Set up all pooled things:
    new PlayerBullet(this.currentGameState, this);

    let chunkNumber = 2;
    if (isChunk1(this.tilemapLevelKey)) {
      chunkNumber = 1;
    } else if (isChunk3(this.tilemapLevelKey)) {
      chunkNumber = 3;
    } else if (isChunk4(this.tilemapLevelKey)) {
      chunkNumber = 4;
    } else if (isChunk5(this.tilemapLevelKey)) {
      chunkNumber = 5;
    } else if (isChunk6(this.tilemapLevelKey)) {
      chunkNumber = 6;
    }

    // Run autotiler
    for (
      let idx = 0;
      idx < this.currentGameState.gameState.board.length;
      idx++
    ) {
      const cell = this.currentGameState.gameState.board[idx];
      for (const entity of cell) {
        if (entity.kind === EntityKind.StaticWall) {
          const board = this.currentGameState.gameState.board;
          attachStaticWall(this, entity, board, seededRandom);
        }
      }

      const floor_checker = (entity: Entities) =>
        entity.kind !== EntityKind.Empty;
      if (cell.every(floor_checker)) {
        let stringPrefix = "Floor/Floor4bit";
        if (chunkNumber === 4) {
          stringPrefix = "Floor/Floor4bit-chunk4_";
        }
        if (chunkNumber === 5 || chunkNumber === 6) {
          stringPrefix = "Floor/Floor4bit-chunk5_";
        }
        const y = Math.floor(idx / 16);
        const x = idx % 16;
        const index =
          autoTile4bit_self_check_ever(
            this.currentGameState.gameState.board,
            x,
            y,
            floor_checker
          ) + 1;
        const sprite = this.add.sprite(
          x * BOARD_SIZE,
          y * BOARD_SIZE,
          "atlas",
          `${stringPrefix}${index}`
        );
        sprite.setActive(false);
        if (
          hasKind_some_test(
            this.currentGameState.gameState.board,
            x,
            y + 1,
            (e) => e.kind === EntityKind.Empty
          )
        ) {
          addFloor({
            x,
            y,
            scene: this,
            random: seededRandom,
            chunkNumber,
          });
        } else {
          // const underFloor = this.add.sprite(
          //   x * BOARD_SIZE,
          //   y * BOARD_SIZE,
          //   "atlas",
          //   `under_floor/under_floor_orange_1`
          // );
          // underFloor.setDepth(y + layerDepthDiff.FLOOR - 0.1);
          // underFloor.setActive(false);
        }

        let hueSat = { hue: 0, sat: 0 };
        if (chunkNumber === 2) {
          hueSat.sat = 0.6;
          hueSat.hue = 0.75;
        }
        if (chunkNumber === 3) {
          hueSat.sat = 0.6;
          hueSat.hue = 0;
        }

        let lightness = seededRandom.weightedPick([
          1,
          0.95,
          0.97,
          0.94,
          0.93,
          0.95,
          0.97,
          0.94,
          0.89,
        ]);
        if (chunkNumber === 6) {
          lightness -= 0.05;
        }

        const color = Phaser.Display.Color.HSLToColor(
          hueSat.hue,
          hueSat.sat,
          lightness
        );
        sprite.setTint(color.color32);

        if (seededRandom.frac() > 0.7) {
          const sprite_texture = this.add.sprite(
            x * BOARD_SIZE + 4,
            y * BOARD_SIZE + 4,
            "atlas",
            `Floor/FloorTextures${seededRandom.integerInRange(1, 18)}`
          );

          sprite_texture.flipX = seededRandom.pick([true, false]);
          sprite_texture.flipY = seededRandom.pick([true, false]);

          sprite_texture.setActive(false);
          sprite_texture.setDepth(y + layerDepthDiff.FLOOR);
          sprite_texture.setTint(color.color32);
          sprite_texture.setAlpha(0.1 + seededRandom.frac() * 0.3);
          sprite_texture.setBlendMode(Phaser.BlendModes.MULTIPLY);
        }
        sprite.setDepth(y + layerDepthDiff.FLOOR);
      }
    }

    // Sets up the undo manager to finalize turn at the end of a tick.
    // Hack... we may need to set a timeout here.
    // This timeout might also need to set the debounce time for when the next
    // input can happen.
    this.events.on(GameEvents.PlayerMadeTurn, this.finalizeTurn, this);
    this.events.on(GameEvents.FX_Explosion, this.addFXexplosion, this);
    this.events.once("destroy", () => {
      this.events.removeListener(
        GameEvents.PlayerMadeTurn,
        this.finalizeTurn,
        this
      );
      GlobalEvents.removeListener(EGlobalEvent.WonLevel, wonLevelHandler, this);
      this.events.removeListener(
        GameEvents.FX_Explosion,
        this.addFXexplosion,
        this
      );
    });

    if (DEV_MODE) {
      getDevEmitter()?.on(dev_event.REQUEST_SNAPSHOT, () => {
        this.game.renderer.snapshot((image) => {
          const imgDOM = image as HTMLImageElement;
          imgDOM.style.imageRendering = "pixelated";
          imgDOM.style.width = `10vw`;
          imgDOM.style.height = `auto`;
          document.body.append(imgDOM);
        });
      });
    }
  }

  finalizeTurn() {
    // Add temporary components.
    SoundPlayer.clearPlayedThisTurn();
    ((this as any).myInput as TurnBasedInputPlugin).recordKey();
    this.currentGameState.gameTurnLoopPostPlayerInteraction();
  }

  update() {
    if (((this as any).myInput as TurnBasedInputPlugin).isUndoPressed) {
      if (this.currentGameState.undoState()) {
        SoundPlayer.clickSFX(this, 0.2);
      }
      ((this as any).myInput as TurnBasedInputPlugin).recordKey();
      this.events.emit(GameEvents.UNDO_Pressed);
    }
    if (((this as any).myInput as TurnBasedInputPlugin).isRestartPressed) {
      SoundPlayer.clickSFX(this, 0.2);
      setTimeout(() => {
        GAME_VARIABLES.IS_RESTART = true;
        this.events.emit(GameEvents.PlayerWonLevel, {
          lvlKey: this.tilemapLevelKey,
          restartPressed: true,
        });
        if (!DEV_MODE) {
          DatabaseHandle.getLevel(this.tilemapLevelKey).then((data: any) => {
            sceneNum++;
            const newSceneName = `${SceneName.StartScene}${sceneNum}`;
            this.scene.add(newSceneName, StartScene, false, {
              tilemapKey: data._id,
              entities: data.entities,
            });
            this.scene.transition({
              target: newSceneName,
              duration: 10,
              remove: true,
            });
          });
        }
      }, 0);
    }

    if (((this as any).myInput as TurnBasedInputPlugin).isEscapePressed) {
      sendLeftEvent(this.tilemapLevelKey, this.currentGameState.turn_counter);
      this.transitionToMap();
    }

    this.failureModal.setVisible(!!this.player?.died);
  }

  transitionToMap() {
    if (!DEV_MODE) {
      this.scene.transition({
        target: SceneName.MapScene,
        duration: 10,
        remove: true,
      });
    }
  }

  addFXexplosion(x: number, y: number) {
    if (this.FXexplosionActiveSet.has(getXYindex(x, y))) {
      return;
    }
    const explosion: Phaser.GameObjects.Sprite = this.FXexplosionGroup?.get(
      x * 8 + (Math.random() * 2 - 1),
      y * 8 + (Math.random() * 2 - 1)
    );
    if (!explosion) {
      console.log("no explosions left in pool...");
    }
    explosion.setActive(true).setVisible(false);

    this.FXexplosionActiveSet.add(getXYindex(x, y));
    let delayStart = Math.random() * 150;
    this.time.delayedCall(delayStart, () => {
      explosion
        .setDepth(explosion.y)
        .setVisible(true)
        .play(data.animations.explosion_fire.key)
        .once("animationcomplete", () => {
          this.FXexplosionGroup?.killAndHide(explosion);
          this.FXexplosionActiveSet.delete(getXYindex(x, y));
        });
    });
  }
}
